<template>
  <b-form>
    <b-alert :show="isActiveEdit" variant="info" class="small">
      ชุดบัตรนี้เปิดใช้แล้ว บัตรอยู่ในมือผู้รับแล้ว — แก้ได้เฉพาะชื่อแคมเปญ ที่มา
      และเลื่อนวันหมดอายุให้นานขึ้น
    </b-alert>
    <h6 class="text-primary border-bottom pb-1 mb-2">
      <i class="fas fa-info-circle mr-1"></i> ข้อมูลชุดบัตร
    </h6>
    <b-form-group label="ชื่อแคมเปญ *" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-input v-model="form.name" required size="sm" />
    </b-form-group>
    <b-form-group
      label="คำนำหน้ารหัส *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="prefixError ? false : null"
      :invalid-feedback="prefixError"
    >
      <b-form-input
        v-model="form.codePrefix"
        :disabled="isEdit"
        :state="prefixError ? false : null"
        style="text-transform: uppercase"
        size="sm"
      />
    </b-form-group>
    <!-- Who the cards were minted for is fixed once the batch exists, and the
         loaded batch does not carry it — so the whole choice is create-only. -->
    <b-form-group v-if="!isEdit" label="ออกบัตรให้ *" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="mintMode" size="sm" stacked>
        <b-form-radio value="BEARER">ใครก็ใช้ได้ (ระบุจำนวนใบ)</b-form-radio>
        <b-form-radio value="GROUP">เฉพาะกลุ่มคนไข้ (1 ใบ ต่อ 1 คน)</b-form-radio>
        <b-form-radio value="LIST">เฉพาะคนไข้ที่เลือก (1 ใบ ต่อ 1 คน)</b-form-radio>
      </b-form-radio-group>
      <small v-if="mintMode !== 'BEARER'" class="text-muted d-block mt-1">
        บัตรที่ผูกกับคนไข้ คนอื่นใช้ไม่ได้ · จำนวนใบจะเท่ากับจำนวนคนไข้ที่เลือก
      </small>
    </b-form-group>

    <b-form-group
      v-if="isEdit || mintMode === 'BEARER'"
      label="จำนวนใบ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="quantityError ? false : null"
      :invalid-feedback="quantityError"
    >
      <b-form-input
        v-model.number="form.quantity"
        :disabled="isEdit"
        type="number"
        min="1"
        :state="quantityError ? false : null"
        size="sm"
      />
    </b-form-group>

    <!-- Single group, not the multiselect PatientGroupPicker used for the
         discount RESTRICTION further down: this picks who cards are MINTED
         for, and two groups would need a documented de-duplication rule
         nobody has asked for. -->
    <b-form-group
      v-if="!isEdit && mintMode === 'GROUP'"
      label="กลุ่มคนไข้ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="mintError ? false : null"
      :invalid-feedback="mintError"
    >
      <b-form-select
        v-model="assignPatientGroupId"
        :options="groupOptions"
        size="sm"
        :state="mintError ? false : null"
      />
    </b-form-group>

    <b-form-group
      v-if="!isEdit && mintMode === 'LIST'"
      label="เลือกคนไข้ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="mintError ? false : null"
      :invalid-feedback="mintError"
    >
      <b-form-radio-group
        v-model="patientScope"
        size="sm"
        class="mb-1"
        @change="searchPatients"
      >
        <b-form-radio value="BRANCH">เฉพาะสาขานี้</b-form-radio>
        <b-form-radio value="CLINIC">ทุกสาขาในคลินิก</b-form-radio>
      </b-form-radio-group>
      <b-form-input
        v-model="patientSearch"
        placeholder="ค้นหาคนไข้ (ชื่อ หรือ HN)"
        size="sm"
        @input="searchPatients"
      />
      <b-list-group v-if="patientResults.length" class="mt-1" style="max-height: 160px; overflow-y: auto">
        <b-list-group-item
          v-for="p in patientResults"
          :key="p.id"
          button
          class="py-1"
          @click="addPatient(p)"
        >
          {{ p.firstNameTh }} {{ p.lastNameTh }}
          <small class="text-muted ml-1">{{ p.dn }}</small>
        </b-list-group-item>
      </b-list-group>
      <div v-if="selectedPatients.length" class="mt-2">
        <b-badge
          v-for="p in selectedPatients"
          :key="p.id"
          variant="light"
          class="mr-1 mb-1 border"
        >
          {{ p.firstNameTh }} {{ p.lastNameTh }}
          <a href="#" class="ml-1 text-danger" @click.prevent="removePatient(p)">&times;</a>
        </b-badge>
        <small class="text-muted d-block">เลือกแล้ว {{ selectedPatients.length }} คน</small>
      </div>
    </b-form-group>
    <b-form-group label="ที่มา / ช่องทางการแจก" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-textarea v-model="form.channelNote" rows="2" size="sm" />
    </b-form-group>

    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-calendar-alt mr-1"></i> ช่วงเวลาที่ใช้ได้
    </h6>
    <b-form-group label="วันเริ่มใช้" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-datepicker
        v-model="form.validFrom"
        :disabled="isActiveEdit"
        reset-button
        placeholder="ไม่กำหนด"
        size="sm"
      />
    </b-form-group>
    <b-form-group
      label="วันหมดอายุ"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="dateError ? false : null"
      :invalid-feedback="dateError"
    >
      <b-form-datepicker
        v-model="form.validTo"
        :min="validToMin"
        :disabled="validToLocked"
        reset-button
        placeholder="ไม่กำหนด"
        size="sm"
      />
      <small v-if="validToLocked" class="text-muted">ชุดบัตรนี้ไม่มีวันหมดอายุ — กำหนดเพิ่มภายหลังไม่ได้</small>
    </b-form-group>

    <fieldset :disabled="isActiveEdit" :class="{ 'voucher-locked': isActiveEdit }">
    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-calculator mr-1"></i> มูลค่าส่วนลด
    </h6>
    <b-form-group label="ขอบเขต" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.scope" :options="scopeOptions" :disabled="isEdit" size="sm" />
    </b-form-group>
    <b-form-group label="ประเภทส่วนลด" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.discountType" :options="typeOptions" size="sm" />
      <!-- Both types are legal for both scopes, so the form never blocks the
           combination — it states the one limitation the campaign owner has to
           price in themselves before printing the cards. -->
      <small v-if="isUnboundedReceiptPercent" class="text-warning d-block mt-1">
        ส่วนลด % ทั้งใบเสร็จไม่มีเพดานเป็นจำนวนเงิน — บัตรใบเดียวกันอาจลด 300 บาท
        หรือ 30,000 บาท ขึ้นกับยอดบิล ถ้าต้องการจำกัดมูลค่าต่อใบ ให้ใช้บัตรแบบจำนวนเงินแทน
      </small>
    </b-form-group>
    <b-form-group
      label="มูลค่า *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="valueError ? false : null"
      :invalid-feedback="valueError"
    >
      <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
        <b-form-input
          v-model.number="form.discountValue"
          type="number"
          min="0"
          :state="valueError ? false : null"
          required
        />
      </b-input-group>
    </b-form-group>
    <b-form-group label="ผู้รับภาระ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-radio-group v-model="form.discountMethod" :options="methodOptions" size="sm" />
    </b-form-group>
    <template v-if="isSpecificMethod">
      <b-form-group label="ส่วนที่หมอรับ" label-cols-sm="3" label-size="sm" class="mb-2">
        <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
          <b-form-input v-model.number="form.doctorDiscountValue" type="number" min="0" />
        </b-input-group>
      </b-form-group>
      <b-form-group label="ส่วนที่คลินิกรับ" label-cols-sm="3" label-size="sm" class="mb-2">
        <b-input-group size="sm" :append="form.discountType === 0 ? '%' : 'บาท'">
          <b-form-input v-model.number="form.clinicDiscountValue" type="number" min="0" />
        </b-input-group>
      </b-form-group>
    </template>
    <b-form-group
      :label="maxPercentLabel"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
    >
      <b-form-input v-model.number="form.maxDiscountPercent" type="number" min="0" max="100" placeholder="ไม่จำกัด" size="sm" />
      <small class="text-muted">เกินเพดาน = ใช้บัตรไม่ได้ (ระบบไม่ตัดยอดลงมาให้เท่าเพดาน)</small>
    </b-form-group>
    <b-form-group label="ยอดใบเสร็จขั้นต่ำ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-input v-model.number="form.minReceiptAmount" type="number" min="0" placeholder="ไม่กำหนด" size="sm" />
    </b-form-group>

    <h6 class="text-primary border-bottom pb-1 mb-2 mt-3">
      <i class="fas fa-sliders-h mr-1"></i> เงื่อนไขการใช้
    </h6>
    <b-form-group label="จำกัดสาขา" label-cols-sm="3" label-size="sm" class="mb-2">
      <BranchPicker v-model="form.branchIds" />
      <small class="text-muted">ไม่เลือก = ใช้ได้ทุกสาขา</small>
    </b-form-group>
    <b-form-group label="เฉพาะแพทย์" label-cols-sm="3" label-size="sm" class="mb-2">
      <DoctorPicker v-model="form.doctorIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>
    <b-form-group label="เฉพาะกลุ่มผู้ป่วย" label-cols-sm="3" label-size="sm" class="mb-2">
      <PatientGroupPicker v-model="form.patientGroupIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>
    <b-form-group label="จำกัดรายการรักษา" label-cols-sm="3" label-size="sm" class="mb-2">
      <TreatmentPicker v-model="form.treatments" :clinic-url="clinicUrl" :branch-url="branchUrl" />
    </b-form-group>
    </fieldset>

    <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

    <div class="d-flex justify-content-end">
      <b-button variant="light" class="mr-2" @click="$emit('cancel')">ยกเลิก</b-button>
      <b-button variant="primary" :disabled="!canSubmit" @click="submit">
        {{ isEdit ? "บันทึกการแก้ไข" : "สร้างชุดบัตร" }}
      </b-button>
    </div>
    <small v-if="!isEdit" class="text-muted d-block text-right mt-1">
      ชุดบัตรจะถูกสร้างเป็นสถานะ "ร่าง" — ต้องกดเปิดใช้ก่อนรหัสจึงจะใช้ได้
    </small>
  </b-form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import BranchPicker from "@/components/discount/BranchPicker.vue";
import DoctorPicker from "@/components/discount/DoctorPicker.vue";
import PatientGroupPicker from "@/components/discount/PatientGroupPicker.vue";
import TreatmentPicker from "@/components/discount/TreatmentPicker.vue";
import { batchToForm, buildBatchUpdatePayload } from "@/utils/voucherBatchEdit";

const MAX_QUANTITY = 10000; // mirrors createBatch.js:6

export default {
  name: "VoucherBatchForm",
  components: { BranchPicker, DoctorPicker, PatientGroupPicker, TreatmentPicker },
  props: {
    // A batch from fetchBatchByID (with its discount) puts the form in edit
    // mode; omitted, the form creates a new batch.
    batch: { type: Object, default: null },
  },
  data() {
    return {
      isSaving: false,
      errorMessage: "",
      // BEARER keeps today's behaviour exactly: a quantity of anonymous codes.
      // GROUP/LIST mint one code per patient, each redeemable only by them.
      mintMode: "BEARER",
      assignPatientGroupId: null,
      patientSearch: "",
      // See the note on searchPatients: BRANCH sends branchId, CLINIC omits it.
      patientScope: "BRANCH",
      patientResults: [],
      selectedPatients: [],
      form: {
        name: "",
        codePrefix: "",
        quantity: 100,
        scope: "RECEIPT",
        channelNote: "",
        validFrom: null,
        validTo: null,
        discountType: 1,
        discountValue: 0,
        discountMethod: 0,
        doctorDiscountValue: null,
        clinicDiscountValue: null,
        maxDiscountPercent: null,
        branchIds: [],
        doctorIds: [],
        patientGroupIds: [],
        minReceiptAmount: null,
        treatments: [],
      },
      scopeOptions: [
        { text: "ทั้งใบเสร็จ", value: "RECEIPT" },
        { text: "รายหัตถการ", value: "ITEM" },
      ],
      typeOptions: [
        { text: "เปอร์เซ็นต์", value: 0 },
        { text: "บาท", value: 1 },
      ],
      methodOptions: [
        { text: "ตามสัดส่วน", value: 0 },
        { text: "ระบุเอง", value: 1 },
      ],
    };
  },
  computed: {
    clinicUrl() { return this.$route.params.clinicUrl; },
    branchUrl() { return this.$route.params.branchUrl; },
    isEdit() { return !!this.batch; },
    // ACTIVE cards are in circulation: only name, note and a later validTo may
    // change (voucherRepo.updateBatch enforces the same rule server-side).
    isActiveEdit() { return this.isEdit && this.batch.status === "ACTIVE"; },
    originalValidTo() { return this.isEdit ? batchToForm(this.batch).validTo : null; },
    validToMin() { return this.isActiveEdit ? this.originalValidTo : null; },
    // Giving an open-ended ACTIVE batch an expiry would shorten it.
    validToLocked() { return this.isActiveEdit && !this.originalValidTo; },
    isReceiptScope() { return this.form.scope === "RECEIPT"; },
    // A percentage off a whole bill is the one campaign shape whose card value
    // cannot be printed: it is worth whatever the receipt happens to total.
    // maxDiscountPercent cannot bound it either — that ceiling is a ratio of the
    // same total, so it either never trips or blocks the card outright — and the
    // data model has no max-baht field. A fixed-baht card is the only bounded
    // one, which is what the warning says instead of offering a fix that isn't.
    isUnboundedReceiptPercent() {
      return this.isReceiptScope && Number(this.form.discountType) === 0;
    },
    maxPercentLabel() {
      return this.isReceiptScope
        ? "เพดานส่วนลดต่อใบเสร็จ (%)"
        : "เพดานส่วนลดต่อบรรทัด (%)";
    },
    isSpecificMethod() { return Number(this.form.discountMethod) === 1; },
    // The input is uppercased by CSS (text-transform) and by buildPayload, so
    // validate what will actually be SENT, not the raw keystrokes. Testing the
    // raw model made "promo" — which reads PROMO on screen — fail with "ต้องเป็น
    // ตัวอักษรอังกฤษพิมพ์ใหญ่" and no visible way to satisfy it. Trim likewise:
    // buildPayload trims, so a trailing space must not block submit either.
    normalizedPrefix() {
      return (this.form.codePrefix || "").trim().toUpperCase();
    },
    prefixError() {
      if (this.isEdit || !this.normalizedPrefix) return "";
      return /^[A-Z]{2,20}$/.test(this.normalizedPrefix)
        ? "" : "คำนำหน้าต้องเป็นตัวอักษรอังกฤษพิมพ์ใหญ่ 2–20 ตัว";
    },
    ...mapGetters("modulePatientGroup", ["getGroupList"]),
    ...mapGetters("moduleBranch", { branchInfo: "getBranchInfo" }),
    groupOptions() {
      return [
        { value: null, text: "— เลือกกลุ่ม —" },
        ...(this.getGroupList || []).map(g => ({
          value: g.id,
          text: `${g.name} (${g.memberCount || 0} คน)`,
        })),
      ];
    },
    mintError() {
      if (this.mintMode === "GROUP" && !this.assignPatientGroupId) {
        return "กรุณาเลือกกลุ่มคนไข้";
      }
      if (this.mintMode === "LIST" && this.selectedPatients.length === 0) {
        return "กรุณาเลือกคนไข้อย่างน้อย 1 คน";
      }
      if (this.selectedPatients.length > MAX_QUANTITY) {
        return `เลือกคนไข้ได้ไม่เกิน ${MAX_QUANTITY.toLocaleString()} คน`;
      }
      return "";
    },
    quantityError() {
      // Only meaningful when creating in BEARER mode — the other two derive the
      // count from the patient list, and an edit never changes the quantity.
      // Returning "" here keeps canSubmit from blocking on a field nobody entered.
      if (this.isEdit || this.mintMode !== "BEARER") return "";
      const q = Number(this.form.quantity);
      if (!Number.isInteger(q) || q < 1 || q > MAX_QUANTITY) {
        return `จำนวนใบต้องเป็นจำนวนเต็ม 1–${MAX_QUANTITY.toLocaleString()}`;
      }
      return "";
    },
    dateError() {
      if (this.isActiveEdit && this.form.validTo && this.form.validTo < this.originalValidTo) {
        return "ชุดบัตรที่เปิดใช้แล้วเลื่อนวันหมดอายุได้เฉพาะให้นานขึ้น";
      }
      if (!this.form.validFrom || !this.form.validTo) return "";
      return this.form.validFrom > this.form.validTo
        ? "วันเริ่มใช้ต้องไม่เกินวันหมดอายุ" : "";
    },
    valueError() {
      const value = Number(this.form.discountValue);
      if (!Number.isFinite(value) || value <= 0) return "มูลค่าส่วนลดต้องมากกว่า 0";
      // The server stores >100 happily but the calculation layer clamps it at
      // redemption, so a "150%" campaign would report 150 and behave as 100.
      // Refuse it here — this is the last screen that can still say so.
      if (Number(this.form.discountType) === 0 && value > 100) {
        return "ส่วนลดแบบเปอร์เซ็นต์ต้องไม่เกิน 100";
      }
      return "";
    },
    canSubmit() {
      // normalizedPrefix, not form.codePrefix: a whitespace-only entry is truthy
      // raw but empty once trimmed, and prefixError stays silent on empty — that
      // combination would enable submit and post an empty prefix.
      return !this.isSaving && !!this.form.name.trim()
        && (this.isEdit || !!this.normalizedPrefix)
        && !this.prefixError && !this.quantityError && !this.dateError
        && !this.valueError && (this.isEdit || !this.mintError);
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["createBatch", "updateBatch"]),
    ...mapActions("modulePatientGroup", ["fetchGroupList"]),

    // Clinic-wide search is the ABSENCE of branchId, not a different route:
    // getPatientList always confines results to the clinic the caller's branch
    // membership authorises, and patient_repo does `if (branchId)` — so leaving
    // it off widens to every branch. Both scopes return bindable patients,
    // since the BE accepts any patient in the clinic as a holder.
    async searchPatients() {
      if (this.patientSearch.length < 2) {
        this.patientResults = [];
        return;
      }
      try {
        const params = { keyword: this.patientSearch, limit: 20 };
        if (this.patientScope === "BRANCH" && this.branchInfo?.id) {
          params.branchId = this.branchInfo.id;
        }
        const res = await axios.get(
          `/api/patient/${this.$route.params.clinicUrl}/${this.$route.params.branchUrl}`,
          { params }
        );
        const found = res.data?.patients || res.data || [];
        const chosen = this.selectedPatients.map(p => p.id);
        this.patientResults = found.filter(p => !chosen.includes(p.id));
      } catch {
        this.patientResults = [];
      }
    },

    addPatient(p) {
      // Guarded here as well as on the BE: a duplicate would be rejected at
      // save with the whole form's worth of input to re-enter.
      if (this.selectedPatients.some(x => x.id === p.id)) return;
      this.selectedPatients.push(p);
      this.patientSearch = "";
      this.patientResults = [];
    },

    removePatient(p) {
      this.selectedPatients = this.selectedPatients.filter(x => x.id !== p.id);
    },


    buildPayload() {
      const f = this.form;
      const data = {
        name: f.name.trim(),
        codePrefix: this.normalizedPrefix,
        scope: f.scope,
        discountType: Number(f.discountType),
        discountValue: Number(f.discountValue),
        discountMethod: Number(f.discountMethod),
      };
      // Exactly ONE of quantity / assignPatientGroupId / assignPatientIds is
      // ever sent. The BE rejects a quantity alongside a patient source,
      // because the two disagree about how many codes to mint.
      if (this.mintMode === "GROUP") {
        data.assignPatientGroupId = Number(this.assignPatientGroupId);
      } else if (this.mintMode === "LIST") {
        data.assignPatientIds = this.selectedPatients.map(p => p.id);
      } else {
        data.quantity = Number(f.quantity);
      }
      if (f.channelNote) data.channelNote = f.channelNote.trim();
      if (f.validFrom) data.validFrom = f.validFrom;
      if (f.validTo) data.validTo = f.validTo;
      if (this.isSpecificMethod) {
        data.doctorDiscountValue = Number(f.doctorDiscountValue || 0);
        data.clinicDiscountValue = Number(f.clinicDiscountValue || 0);
      }
      // Sent for both scopes: ITEM caps the discount against the line's price,
      // RECEIPT against the eligible lines' post-item-discount total.
      if (f.maxDiscountPercent != null && f.maxDiscountPercent !== "") {
        data.maxDiscountPercent = Number(f.maxDiscountPercent);
      }
      if (f.minReceiptAmount != null && f.minReceiptAmount !== "") {
        data.minReceiptAmount = Number(f.minReceiptAmount);
      }
      if (f.branchIds.length) data.branchIds = f.branchIds;
      if (f.doctorIds.length) data.doctorIds = f.doctorIds;
      if (f.patientGroupIds.length) data.patientGroupIds = f.patientGroupIds;
      if (f.treatments.length) data.treatments = f.treatments;
      return data;
    },

    async submit() {
      if (!this.canSubmit) return;
      this.isSaving = true;
      this.errorMessage = "";
      if (this.isEdit) {
        try {
          const result = await this.updateBatch({
            clinicUrl: this.$route.params.clinicUrl,
            batchId: this.batch.id,
            data: buildBatchUpdatePayload(this.form, this.batch),
          });
          this.$emit("updated", result);
        } catch (error) {
          this.errorMessage = error?.response?.data?.message || "บันทึกการแก้ไขไม่สำเร็จ";
        } finally {
          this.isSaving = false;
        }
        return;
      }
      try {
        const result = await this.createBatch({
          clinicUrl: this.$route.params.clinicUrl,
          data: this.buildPayload(),
        });
        this.$emit("created", result);
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "สร้างชุดบัตรไม่สำเร็จ";
      } finally {
        this.isSaving = false;
      }
    },
  },
  // PatientGroupPicker (the discount RESTRICTION picker further down this form)
  // fetches the same list on its own created(), but this form's GROUP mode
  // renders whether or not that picker is mounted, so it cannot rely on it.
  // fetchGroupList commits to a shared store module, so the duplicate call is
  // idempotent rather than a second list.
  created() {
    if (this.batch) this.form = { ...this.form, ...batchToForm(this.batch) };
    this.fetchGroupList({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
    });
  },
};
</script>

<style scoped>
/* fieldset[disabled] stops native inputs but not the div-based pickers
   (vue-multiselect), so block pointer input to the whole locked block. */
.voucher-locked {
  opacity: 0.6;
  pointer-events: none;
}
</style>
